import { get } from "lodash";
import pesService from "@tvg/api/pes";
import { isXSell } from "@tvg/sh-utils/mobileUtils";
import { fetchStoryblok } from "@tvg/sh-lib-storyblok/src/fetchStoryblok";
import { STORYBLOK_CONTENT_PATH } from "@tvg/sh-lib-storyblok/src/constraints/config";
import {
  StoryblokEnvironment,
  StoryblokStory,
  StoryblokVersion
} from "@tvg/sh-lib-storyblok/src/types";
import { HomepageOrder } from "@fdr/types/ts/Home";
import { HeaderBanner, BannerContext } from "@urp/derby-banner/src/types";
import defaultOrder from "../constraints/defaultOrder";
import {
  HomepageConfig,
  HomepageConfigType,
  HomepagePromo,
  HomepageStoryblokContent
} from "./types";
import { ComponentConfig } from "../constraints/types";

export const getHomepageConfigByPES = async (): Promise<
  Array<ComponentConfig>
> => {
  try {
    return (await pesService.fetchHomepageConfiguration()) || defaultOrder;
  } catch (e) {
    console.warn("Error retrieving homepage config by PES: ", e);
    return defaultOrder;
  }
};

const getFormattedHomepage = (
  storyblokComponent: StoryblokStory<HomepageStoryblokContent>,
  eventType: string,
  defaultValue: Array<ComponentConfig> = []
): HomepageOrder => ({
  header: get(
    storyblokComponent,
    `content.${eventType}_homepage_header_order`,
    []
  ),
  body: get(
    storyblokComponent,
    `content.${eventType}_homepage_order`,
    defaultValue
  ),
  unmapped: []
});

export const getDerbyBanner = (
  storyblokComponent: StoryblokStory<HomepageStoryblokContent>,
  eventType: string
): Array<HeaderBanner> =>
  get(storyblokComponent, `content.${eventType}_homepage_banner`, []).filter(
    (banner: { context: BannerContext }) =>
      banner.context.includes(
        isXSell() ? BannerContext.X_SELL : BannerContext.MOBILE_SA
      )
  );

const formatDate = (storyblokDate: string) => {
  if (!storyblokDate) {
    return null;
  }

  const [date, time] = storyblokDate.split(" ");
  return new Date(`${date}T${time}:00Z`);
};

export const getHomepageConfigByStoryblok = async (
  storyblokVersion = StoryblokVersion.DRAFT,
  customOptions: Object = {},
  overrideToken?: StoryblokEnvironment
): Promise<HomepageConfig> => {
  try {
    const storyblokResponse = await fetchStoryblok<HomepageStoryblokContent>(
      STORYBLOK_CONTENT_PATH.HOMEPAGE_CONFIG,
      "homepage-config",
      storyblokVersion,
      overrideToken
    );

    const storyblokDefaultOrder = getFormattedHomepage(
      storyblokResponse,
      "default",
      defaultOrder
    );

    const storyblokEventDayOrder =
      getFormattedHomepage(storyblokResponse, "event_day") ||
      storyblokDefaultOrder;

    const storyblokPreEventDayOrder =
      getFormattedHomepage(storyblokResponse, "pre_event") ||
      storyblokDefaultOrder;

    const storyblokFridayPreEventDayOrder =
      getFormattedHomepage(storyblokResponse, "friday_pre_event") ||
      storyblokDefaultOrder;

    const storyblokThursdayPreEventDayOrder =
      getFormattedHomepage(storyblokResponse, "thursday_pre_event") ||
      storyblokDefaultOrder;

    const derbyBannerDefault = getDerbyBanner(storyblokResponse, "default");
    const derbyBannerEventDay = getDerbyBanner(storyblokResponse, "event_day");
    const derbyBannerPreEvent = getDerbyBanner(storyblokResponse, "pre_event");
    const derbyBannerFridayPreEvent = getDerbyBanner(
      storyblokResponse,
      "friday_pre_event"
    );
    const derbyBannerThursdayPreEvent = getDerbyBanner(
      storyblokResponse,
      "thursday_pre_event"
    );

    const eventDate = formatDate(get(storyblokResponse, "content.event_date"));

    const eventName: string = get(storyblokResponse, "content.event_name", "");
    const eventNameSize: number = +get(
      storyblokResponse,
      "content.event_name_size",
      ""
    );

    const eventSubtitle: string = get(
      storyblokResponse,
      "content.event_subtitle",
      ""
    );
    const eventSubtitleSize: number = +get(
      storyblokResponse,
      "content.event_subtitle_size",
      ""
    );

    const eventDescription: string = get(
      storyblokResponse,
      "content.event_description",
      ""
    );
    const eventDescriptionSize: number = +get(
      storyblokResponse,
      "content.event_description_size",
      ""
    );

    const trackAbbr: string = get(storyblokResponse, "content.track_abbr", "");
    const raceNumber: number = +get(
      storyblokResponse,
      "content.event_race_number",
      ""
    );

    const promo: HomepagePromo = get(
      storyblokResponse,
      "content.event_promo[0]",
      {
        label: "",
        link: ""
      }
    );

    const eventDayCtaButton = get(
      storyblokResponse,
      "content.event_day_homepage_cta[0]",
      null
    );

    const preEventCtaButton = get(
      storyblokResponse,
      "content.pre_event_homepage_cta[0]",
      null
    );

    return {
      order: {
        default: storyblokDefaultOrder,
        eventDay: storyblokEventDayOrder,
        fridayPreEvent: storyblokFridayPreEventDayOrder,
        thursdayPreEvent: storyblokThursdayPreEventDayOrder,
        preEvent: storyblokPreEventDayOrder
      },
      derbyBanner: {
        default: derbyBannerDefault,
        eventDay: derbyBannerEventDay,
        fridayPreEvent: derbyBannerFridayPreEvent,
        thursdayPreEvent: derbyBannerThursdayPreEvent,
        preEvent: derbyBannerPreEvent
      },
      eventInfo: {
        eventDate,
        eventName: {
          label: eventName,
          size: eventNameSize
        },
        eventSubtitle: {
          label: eventSubtitle,
          size: eventSubtitleSize
        },
        eventDescription: {
          label: eventDescription,
          size: eventDescriptionSize
        },
        trackAbbr,
        raceNumber,
        promo
      },
      ctaButtons: {
        eventDay: eventDayCtaButton,
        preEvent: preEventCtaButton
      },
      currentConfig: HomepageConfigType.DEFAULT
    };
  } catch (e) {
    console.warn(
      "Error retrieving homepage config by Storyblok, trying to use PES: ",
      e
    );

    const fallbackConfig = await getHomepageConfigByPES();
    return {
      order: {
        default: {
          header: [],
          body: [],
          unmapped: fallbackConfig
        }
      },
      eventInfo: get(customOptions, "pesEventInfo.event"),
      currentConfig: HomepageConfigType.DEFAULT,
      ctaButtons: get(customOptions, "pesEventInfo.cta", {})
    };
  }
};
